import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Upload, X, Image as ImageIcon, Video, Euro, FileText, Camera, Film, Trash2, Lightbulb, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { ContentRulesWarning, RulesAcceptanceCheckbox } from '../components/ContentRules';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const currencies = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' }
];

const CreatePost = () => {
  const { token, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount_needed: '',
    currency: 'EUR',
    category: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error(language === 'it' ? 'Massimo 5 immagini' : 'Maximum 5 images');
      return;
    }
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'it' ? 'Immagine troppo grande (max 5MB)' : 'Image too large (max 5MB)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, { data: reader.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error(language === 'it' ? 'Video troppo grande (max 50MB)' : 'Video too large (max 50MB)');
      return;
    }
    
    setVideoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.amount_needed || !formData.category) {
      toast.error(language === 'it' ? 'Compila tutti i campi obbligatori' : 'Fill all required fields');
      return;
    }

    if (images.length === 0) {
      toast.error(language === 'it' ? 'Aggiungi almeno un\'immagine di copertina' : 'Add at least one cover image');
      return;
    }

    if (formData.description.length < 50) {
      toast.error(language === 'it' ? 'La descrizione deve essere almeno 50 caratteri' : 'Description must be at least 50 characters');
      return;
    }

    if (!rulesAccepted) {
      toast.error(language === 'it' ? 'Devi accettare le regole della community' : 'You must accept the community rules');
      return;
    }

    setLoading(true);
    try {
      const postData = {
        ...formData,
        amount_needed: parseFloat(formData.amount_needed),
        images: images.map(img => img.data),
        video_url: videoPreview || null
      };

      await axios.post(`${API_URL}/api/posts`, postData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'it' ? 'La tua storia è stata pubblicata!' : 'Your story has been published!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'it' ? 'Errore nella pubblicazione' : 'Error publishing'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t('back')}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <FileText className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-stone-900">
                {language === 'it' ? 'Racconta la tua storia' : 'Share your story'}
              </h1>
              <p className="text-stone-600">
                {language === 'it' ? 'Spiega di cosa hai bisogno e perché' : 'Explain what you need and why'}
              </p>
            </div>
          </div>

          {/* Small Amount Advisory Banner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5"
            data-testid="small-amount-advisory"
          >
            <div className="flex gap-4">
              <div className="shrink-0 w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <h4 className="font-heading font-bold text-emerald-900 text-base mb-1">
                  {language === 'it' ? 'Consiglio importante' 
                    : language === 'fr' ? 'Conseil important'
                    : language === 'de' ? 'Wichtiger Hinweis'
                    : language === 'es' ? 'Consejo importante'
                    : language === 'zh' ? '重要建议'
                    : 'Important tip'}
                </h4>
                <p className="text-emerald-800 text-sm leading-relaxed">
                  {language === 'it' ? 'Le richieste di piccoli importi hanno una probabilità molto più alta di ricevere donazioni. Ti consigliamo di chiedere importi contenuti e ragionevoli — i donatori sono più propensi ad aiutare quando la cifra è accessibile.' 
                    : language === 'fr' ? 'Les demandes de petits montants ont beaucoup plus de chances de recevoir des dons. Nous vous conseillons de demander des montants modestes et raisonnables.'
                    : language === 'de' ? 'Anfragen mit kleinen Beträgen haben eine viel höhere Chance, Spenden zu erhalten. Wir empfehlen Ihnen, bescheidene und angemessene Beträge anzufragen.'
                    : language === 'es' ? 'Las solicitudes de pequeñas cantidades tienen muchas más probabilidades de recibir donaciones. Te aconsejamos pedir cantidades modestas y razonables.'
                    : language === 'zh' ? '小额请求更有可能收到捐赠。我们建议您请求适度合理的金额。'
                    : 'Requests for small amounts are much more likely to receive donations. We advise you to ask for modest and reasonable amounts — donors are more willing to help when the amount is accessible.'}
                </p>
              </div>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-base font-semibold">
                {language === 'it' ? 'Titolo della tua richiesta' : 'Title of your request'} *
              </Label>
              <Input
                id="title"
                data-testid="post-title-input"
                placeholder={language === 'it' ? 'Es: Ho bisogno di aiuto per le spese mediche di mia figlia' : 'E.g.: I need help with my daughter\'s medical expenses'}
                className="h-12 rounded-xl border-stone-200 bg-stone-50 focus:bg-white"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                {language === 'it' ? 'Categoria' : 'Category'} *
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="post-category-select" className="h-12 rounded-xl border-stone-200">
                  <SelectValue placeholder={language === 'it' ? 'Seleziona una categoria' : 'Select a category'} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {language === 'it' ? cat.name_it : cat.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-base font-semibold">
                  {language === 'it' ? 'Quanto ti serve?' : 'How much do you need?'} *
                </Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="amount"
                    data-testid="post-amount-input"
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="0.00"
                    className="pl-10 h-12 rounded-xl border-stone-200 bg-stone-50 focus:bg-white"
                    value={formData.amount_needed}
                    onChange={(e) => setFormData({ ...formData, amount_needed: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">{language === 'it' ? 'Valuta' : 'Currency'}</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger data-testid="post-currency-select" className="h-12 rounded-xl border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map(cur => (
                      <SelectItem key={cur.code} value={cur.code}>
                        {cur.symbol} {cur.code} - {cur.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Story/Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-base font-semibold">
                {language === 'it' ? 'La tua storia' : 'Your story'} *
              </Label>
              <p className="text-sm text-stone-500 mb-2">
                {language === 'it' 
                  ? 'Racconta la tua situazione in modo dettagliato. Spiega perché hai bisogno di aiuto e come verranno utilizzati i fondi.' 
                  : 'Describe your situation in detail. Explain why you need help and how the funds will be used.'}
              </p>
              <Textarea
                id="description"
                data-testid="post-description-input"
                placeholder={language === 'it' 
                  ? 'Mi chiamo... e ho bisogno del vostro aiuto perché...' 
                  : 'My name is... and I need your help because...'}
                className="min-h-[200px] rounded-xl border-stone-200 bg-stone-50 focus:bg-white resize-none"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
              <p className="text-sm text-stone-500 text-right">
                {formData.description.length}/50 {language === 'it' ? 'caratteri minimi' : 'min characters'}
              </p>
            </div>

            {/* Media Upload Section */}
            <div className="space-y-4 pt-4 border-t border-stone-200">
              <h3 className="font-heading text-lg font-semibold text-stone-900">
                {language === 'it' ? 'Aggiungi foto e video' : 'Add photos and videos'}
              </h3>
              <p className="text-sm text-stone-500">
                {language === 'it' 
                  ? 'Le foto e i video aiutano le persone a capire meglio la tua situazione' 
                  : 'Photos and videos help people better understand your situation'}
              </p>

              {/* Image Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-emerald-600" />
                  {language === 'it' ? 'Foto (max 5)' : 'Photos (max 5)'}
                </Label>
                
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  ref={imageInputRef}
                />
                
                <div className="flex flex-wrap gap-3">
                  {images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.data}
                        alt={`Upload ${index + 1}`}
                        className="h-24 w-24 object-cover rounded-xl border-2 border-stone-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  
                  {images.length < 5 && (
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="h-24 w-24 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
                    >
                      <ImageIcon className="h-6 w-6 mb-1" />
                      <span className="text-xs">{language === 'it' ? 'Aggiungi' : 'Add'}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Video Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Film className="h-4 w-4 text-indigo-600" />
                  {language === 'it' ? 'Video (max 50MB)' : 'Video (max 50MB)'}
                </Label>
                
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="hidden"
                  ref={videoInputRef}
                />
                
                {videoPreview ? (
                  <div className="relative rounded-xl overflow-hidden border-2 border-stone-200">
                    <video
                      src={videoPreview}
                      controls
                      className="w-full max-h-64"
                    />
                    <button
                      type="button"
                      onClick={removeVideo}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow-lg hover:bg-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    className="w-full py-8 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                  >
                    <Video className="h-10 w-10 mb-2" />
                    <span className="font-medium">{language === 'it' ? 'Carica un video' : 'Upload a video'}</span>
                    <span className="text-sm mt-1">MP4, MOV, AVI</span>
                  </button>
                )}
              </div>
            </div>

            {/* Rules Warning and Acceptance */}
            <div className="space-y-4 pt-6 border-t border-stone-200">
              <ContentRulesWarning compact />
              <RulesAcceptanceCheckbox 
                accepted={rulesAccepted} 
                onChange={setRulesAccepted} 
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-6 border-t border-stone-200">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                onClick={() => navigate(-1)}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                data-testid="post-submit-btn"
                disabled={loading || !rulesAccepted}
                className="flex-1 h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold"
              >
                {loading ? t('loading') : (language === 'it' ? 'Pubblica la mia storia' : 'Publish my story')}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 bg-amber-50 rounded-2xl border border-amber-200 p-6"
        >
          <h3 className="font-heading font-semibold text-amber-900 mb-3">
            {language === 'it' ? 'Consigli per una storia efficace' : 'Tips for an effective story'}
          </h3>
          <ul className="space-y-2 text-sm text-amber-800">
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>{language === 'it' ? 'Chiedi importi piccoli e ragionevoli — hanno più successo!' : 'Ask for small, reasonable amounts — they\'re more successful!'}</span>
            </li>
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>{language === 'it' ? 'Sii sincero e dettagliato nella tua descrizione' : 'Be honest and detailed in your description'}</span>
            </li>
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>{language === 'it' ? 'Aggiungi foto che mostrano la tua situazione' : 'Add photos that show your situation'}</span>
            </li>
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>{language === 'it' ? 'Un video personale aumenta la fiducia dei donatori' : 'A personal video increases donor trust'}</span>
            </li>
            <li className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>{language === 'it' ? 'Spiega come userai i fondi raccolti' : 'Explain how you will use the funds raised'}</span>
            </li>
          </ul>
        </motion.div>
      </main>
    </div>
  );
};

export default CreatePost;
